import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'

interface LedgerTableProps {
  title: string
  empty: string
  isLoading: boolean
  isDark: boolean
  rows: Array<{ id: string; primary: string; secondary: string; meta: string }>
  total: number
  limit: number
  offset: number
  hasMore: boolean
  filterValue?: string
  filterOptions?: Array<{ value: string; label: string }>
  onFilterChange?: (value: string) => void
  onPageChange: (offset: number) => void
}

export function LedgerTable({
  title,
  empty,
  isLoading,
  isDark,
  rows,
  total,
  limit,
  offset,
  hasMore,
  filterValue,
  filterOptions,
  onFilterChange,
  onPageChange,
}: LedgerTableProps) {
  const mutedText = isDark ? 'text-neutral-500' : 'text-neutral-500'
  const strongText = isDark ? 'text-neutral-100' : 'text-neutral-900'
  const canPrev = offset > 0
  const canNext = hasMore
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + limit, total)

  return (
    <section
      aria-label={title}
      className={cn(
        'rounded-lg border p-4',
        isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50',
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className={cn('text-xs font-semibold uppercase tracking-wider', mutedText)}>
            {title}
          </h3>
          <p className={cn('mt-1 text-[11px]', mutedText)}>
            {isLoading ? 'Loading...' : `${start}-${end} of ${total}`}
          </p>
        </div>

        {filterOptions && onFilterChange && filterValue != null && (
          <select
            value={filterValue}
            onChange={(event) => onFilterChange(event.target.value)}
            className={cn(
              'h-8 rounded-md border px-2 text-xs outline-none',
              isDark
                ? 'border-white/[0.08] bg-surface-1 text-neutral-200'
                : 'border-neutral-200 bg-white text-neutral-800',
            )}
            aria-label={`${title} type filter`}
          >
            {filterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <p className={cn('mt-3 text-sm', mutedText)}>Loading...</p>
      ) : rows.length === 0 ? (
        <p className={cn('mt-3 text-sm', mutedText)}>{empty}</p>
      ) : (
        <div className="mt-3 divide-y divide-neutral-200 dark:divide-white/[0.06]">
          {rows.map((row) => (
            <div key={row.id} className="grid grid-cols-[1fr_auto] gap-3 py-2 first:pt-0 last:pb-0">
              <div>
                <div className={cn('text-sm font-medium', strongText)}>{row.primary}</div>
                <div className={cn('text-xs', mutedText)}>{row.secondary}</div>
              </div>
              <div className={cn('text-sm font-semibold', strongText)}>{row.meta}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(0, offset - limit))}
          disabled={!canPrev || isLoading}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium',
            isDark
              ? 'border-white/[0.08] text-neutral-300 disabled:text-neutral-600'
              : 'border-neutral-200 text-neutral-700 disabled:text-neutral-300',
            (!canPrev || isLoading) && 'cursor-not-allowed opacity-60',
          )}
          aria-label={`${title} previous page`}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Prev
        </button>
        <span className={cn('text-[11px]', mutedText)}>Page {Math.floor(offset / limit) + 1}</span>
        <button
          type="button"
          onClick={() => onPageChange(offset + limit)}
          disabled={!canNext || isLoading}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium',
            isDark
              ? 'border-white/[0.08] text-neutral-300 disabled:text-neutral-600'
              : 'border-neutral-200 text-neutral-700 disabled:text-neutral-300',
            (!canNext || isLoading) && 'cursor-not-allowed opacity-60',
          )}
          aria-label={`${title} next page`}
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  )
}
